import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import styled from "styled-components";
import InputContainer from './InputContainer';
import Input from './Input';
export default React.memo(function CoordinatesInput({ lat, lon, onCoordinates, size, className = '', t }) {
    const commonRegEx = /^(?<lat>[\.,\da-zа-яё°′`'"]+)(?<separator>,?\s+)(?<lon>[\.,\da-zа-яё°′`'"]+)$/i;
    const coordinateRegEx = /^(?<beforeDir>[NSEW])?(?<minusSign>[\-,—])?(?<deg>\d+)(?<middleDir>[NSEW])?(?<decMinGroup>(?<decSep>[\.,])(?<decMin>\d{1,6})?)?(?<degMinGroup>(?<degSign>°)?(?<degMin>\d{1,2})?(?<degMinSign>[′'`])?(?<degSec>\d{1,2})?(?<degSecSign>′{0,2}'{0,2}`{0,2}″?"?)?)?(?<afterDir>[NSEW])?(?<afterWhiteSpace>\s?)?(?<customName>[a-z,а-я\.ё]+)?$/i;
    const latRef = React.useRef(null);
    const lonRef = React.useRef(null);
    const [_lat, _setLat] = React.useState((lat === null || lat === void 0 ? void 0 : lat.toString()) || '');
    const [_lon, _setLon] = React.useState((lon === null || lon === void 0 ? void 0 : lon.toString()) || '');
    React.useEffect(() => {
        _setLat((lat === null || lat === void 0 ? void 0 : lat.toString()) || '');
        _setLon((lon === null || lon === void 0 ? void 0 : lon.toString()) || '');
    }, [lat, lon]);
    const onSubmit = React.useCallback((evt) => {
        var _a, _b;
        evt && evt.preventDefault();
        if (document.activeElement == latRef.current) {
            (_a = lonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            (_b = latRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [latRef.current, lonRef.current]);
    const checkCopyPaste = (input) => {
        const match = input.match(commonRegEx);
        if (!(match === null || match === void 0 ? void 0 : match.groups))
            return false;
        let outputLat = 0;
        let outputLon = 0;
        const { lat, lon } = match.groups;
        const matchLat = matchCoordinate(lat);
        const matchLon = matchCoordinate(lon);
        if (matchLat && isValidCoordinate(matchLat)) {
            outputLat = toDecimal(matchLat);
            _setLat(lat.replace(/,$/, ''));
        }
        if (matchLon && isValidCoordinate(matchLon)) {
            outputLon = toDecimal(matchLon);
            _setLon(lon.replace(/,$/, ''));
        }
        if (outputLat && outputLon) {
            onCoordinates(outputLat, outputLon);
        }
        return Boolean(matchLat && matchLon);
    };
    function matchCoordinate(coordinate) {
        return coordinate.match(coordinateRegEx);
    }
    const isValidCoordinate = (match) => {
        if (!match)
            return false;
        const { deg, beforeDir, middleDir, afterDir, degSign, decMinGroup, degMinGroup } = match.groups;
        const someDir = [beforeDir, middleDir, afterDir].some(dir => dir);
        return Boolean(deg && (degSign || someDir || decMinGroup || degMinGroup));
    };
    const toDecimal = (match) => {
        let result = '';
        if (!match || !match[0])
            return result;
        const { beforeDir, minusSign, deg, middleDir, decMinGroup, decSep, decMin, degMinGroup, degSign, degMin, degMinSign, degSec, afterDir } = match.groups;
        const direction = beforeDir || middleDir || afterDir;
        if (deg && !degMinGroup) {
            result = Number(`${minusSign || ''}${deg}.${decMin || 0}`);
        }
        else if (deg && (direction || degSign || degMinSign)) {
            result = Number((+deg + (degMin ? (+degMin / 60) : 0) + (degSec ? (+degSec / 3600) : 0)).toFixed(6));
            result = ['S', 'W'].includes(direction) ? (0 - result) : result;
        }
        return result;
    };
    const adjust = (val, type) => {
        let result = val.replace(/[%:*<>\+\?!;\[\]\/\\{}\|~@#\$\^&\(\)=№A-Z,А-Я,а-я,ё]/ig, '').replace(/—/g, '-');
        const match = matchCoordinate(result);
        if (!match)
            return result;
        const { minusSign, deg, decSep, decMin } = match === null || match === void 0 ? void 0 : match.groups;
        const maxValue = type === 'lat' ? 90 : 180;
        if (deg && (Number(deg) === maxValue) && decSep) {
            result = result.replace(new RegExp("\\" + decSep), '');
        }
        if (deg && decMin) {
            result = `${minusSign || ''}${deg}.${decMin.slice(0, 6)}`;
        }
        if (deg && (Number(deg) > maxValue)) {
            result = `${type === 'lat' ? (minusSign || '') : ''}${maxValue}`;
        }
        if (type === 'lon') {
            result = result.replace(/[NS]/i, '');
        }
        else if (type === 'lat') {
            result = result.replace(/[WE]/i, '');
        }
        return result;
    };
    const onChangeLat = (val) => {
        if (checkCopyPaste(val))
            return;
        const adjustedVal = adjust(val, 'lat');
        _setLat(adjustedVal);
        const matchLat = matchCoordinate(adjustedVal);
        const matchLon = matchCoordinate(_lon);
        onCoordinates(toDecimal(matchLat), toDecimal(matchLon));
    };
    const onChangeLon = (val) => {
        if (checkCopyPaste(val))
            return;
        const adjustedVal = adjust(val, 'lon');
        _setLon(adjustedVal);
        const matchLon = matchCoordinate(adjustedVal);
        const matchLat = matchCoordinate(_lat);
        onCoordinates(toDecimal(matchLat), toDecimal(matchLon));
    };
    return (_jsxs(Container, { className: `ui-coordinates-input ${className}`, onSubmit: onSubmit, children: [_jsx(InputContainer, { label: t ? t("chronos.app.components.zet.latitude") : "Широта", children: _jsx(Input, { size: size, ref: latRef, value: _lat, onChange: onChangeLat }) }), _jsx(InputContainer, { label: t ? t("chronos.app.components.zet.longitude") : "Долгота", children: _jsx(Input, { size: size, ref: lonRef, value: _lon, onChange: onChangeLon }) })] }));
});
const Container = styled.div `
  display: flex;
  position: relative;
  gap: 1rem;
  color: var(--text-primary);
`;
